// src/contexts/AuthContext.js - Context per autenticazione con supporto offline
import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/api';
import { useOfflineStorage } from '../hooks/useOfflineStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [lastOnlineLogin, setLastOnlineLogin] = useState(null);
  
  const { isOnline } = useOfflineStorage();

  useEffect(() => {
    // Check del token all'avvio con supporto offline
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Aggiorna il token nell'ApiService
          ApiService.token = token;
          
          try {
            // Prova prima online
            const userData = await ApiService.getProfile();
            setUser(userData);
            setIsOfflineMode(false);
            setLastOnlineLogin(Date.now());
          } catch (error) {
            // Se offline o errore di rete, usa strategia hybrid
            if (!isOnline) {              
              // Verifica se abbiamo dati cache recenti
              const cachedProfile = await getCachedAuthProfile();
              if (cachedProfile) {
                setUser(cachedProfile.user);
                setIsOfflineMode(true);
                setLastOnlineLogin(cachedProfile.timestamp);
              } else {
                throw new Error('Nessun profilo cache disponibile');
              }
            } else {
              throw error; // Re-throw se online ma errore API
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Token non valido o cache scaduta, pulisci tutto
        await clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [isOnline]);

  const login = async (email, password) => {
    try {
      // Usa ApiService per il login con fallback offline
      const result = await ApiService.login(email, password);
      
      // Imposta l'utente dal risultato
      const userData = result.agent || result.user;
      setUser(userData);
      
      // Gestione modalità online/offline
      if (result.isOfflineLogin) {
        setIsOfflineMode(true);
        setLastOnlineLogin(new Date(result.lastLoginDate).getTime());
      } else {
        setIsOfflineMode(false);
        setLastOnlineLogin(Date.now());
      }
      
      return result;
    } catch (error) {
      console.error('Login error:', error);
      
      // Se login fallito e offline, prova con cache
      if (!isOnline) {
        try {
          const cachedAuth = await getCachedAuthProfile();
          if (cachedAuth) {
            setUser(cachedAuth.user);
            setIsOfflineMode(true);
            setLastOnlineLogin(cachedAuth.timestamp);
            
            return {
              token: cachedAuth.token,
              agent: cachedAuth.user,
              user: cachedAuth.user,
              isOfflineLogin: true,
              lastLoginDate: new Date(cachedAuth.timestamp).toISOString()
            };
          }
        } catch (cacheError) {
          console.error('Cache login failed:', cacheError);
        }
      }
      
      throw error;
    }
  };

  const register = async (name, email, password) => {
    try {
      // Usa ApiService per la registrazione
      const result = await ApiService.register(name, email, password);
      
      // Imposta l'utente dal risultato
      setUser(result.agent || result.user);
      
      return result;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  };

  const logout = async () => {
    // Pulisci anche cache auth offline
    await clearAuthData();
    setUser(null);
    setIsOfflineMode(false);
    setLastOnlineLogin(null);
  };
  
  // Funzioni helper per gestione cache auth
  const getCachedAuthProfile = async () => {
    try {
      if (!('caches' in window)) return null;
      
      const cache = await caches.open('auth-cache');
      const response = await cache.match('/auth-cache/current');
      
      if (response) {
        const authCache = await response.json();
        const daysSinceLogin = (Date.now() - authCache.timestamp) / (1000 * 60 * 60 * 24);
        
        // Verifica se cache ancora valida (7 giorni max)
        if (daysSinceLogin <= 7) {
          return authCache;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Errore recupero cache auth:', error);
      return null;
    }
  };
  
  const clearAuthData = async () => {
    // Pulisci localStorage
    localStorage.removeItem('token');
    ApiService.logout();
    
    // Pulisci cache Service Worker
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_AUTH_CACHE'
        });
      }
    } catch (error) {
      console.error('Errore pulizia cache SW:', error);
    }
  };
  
  // Sincronizzazione quando torna online
  useEffect(() => {
    const syncAuthWhenOnline = async () => {
      if (isOnline && isOfflineMode && user) {        
        try {
          // Verifica token con server
          const userData = await ApiService.getProfile();
          setUser(userData);
          setIsOfflineMode(false);
          setLastOnlineLogin(Date.now());
        } catch (error) {
          console.error('❌ Sincronizzazione auth fallita:', error);
          // Se token scaduto, forza logout
          if (error.message?.includes('401') || error.message?.includes('token')) {
            await logout();
          }
        }
      }
    };
    
    syncAuthWhenOnline();
  }, [isOnline, isOfflineMode, user]);

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isOfflineMode,
    lastOnlineLogin,
    isOnline
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;